import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineCog, HiOutlinePlus, HiOutlineChevronDown,
  HiOutlineChevronRight, HiOutlinePencil, HiOutlineTrash, HiOutlineCheck, HiOutlineX,
} from 'react-icons/hi';
import { getMongoConfigs, createMongoConfig, updateMongoConfig, deleteMongoConfig } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import './MongoConfiguration.css';

function decodeHtmlEntities(text) {
  return String(text || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function findNearestCodeText(btn) {
  const container = btn.closest(
    '.mgb-cmd-row, .mgb-code-wrap, .mgb-cmd-block, .mgb-step, .mgb-section, .mgb-guide, [class*="cmd"], [class*="code"]'
  );

  if (container) {
    const selectors = [
      '.mgb-cmd-box',
      '.mgb-code',
      '.mgb-script-box',
      '#mgb-script-content',
      'pre',
      'code',
      'input[type="text"]',
    ];

    for (const selector of selectors) {
      const el = container.querySelector(selector);
      if (el && el !== btn && !el.contains(btn)) {
        const value = (el.textContent || el.value || '').trim();
        if (value) return value;
      }
    }
  }

  let sibling = btn.previousElementSibling;
  while (sibling) {
    const value = (sibling.textContent || sibling.value || '').trim();
    if (value && !sibling.contains(btn)) return value;
    sibling = sibling.previousElementSibling;
  }

  return '';
}

function parseOnclickCopyText(onclick) {
  if (!onclick) return '';

  const patterns = [
    /mgbCopy\s*\(\s*this\s*,\s*(['"])([\s\S]*?)\1/,
    /copyToClipboard\s*\(\s*(['"])([\s\S]*?)\1/,
    /copyText\s*\(\s*(['"])([\s\S]*?)\1/,
    /copyCommand\s*\(\s*(['"])([\s\S]*?)\1/,
    /navigator\.clipboard\.writeText\s*\(\s*(['"])([\s\S]*?)\1/,
    /(?:^|[,(])\s*(['"])([\s\S]*?)\1\s*\)\s*;?\s*$/,
  ];

  for (const pattern of patterns) {
    const match = onclick.match(pattern);
    if (match?.[2]) return decodeHtmlEntities(match[2]);
  }

  return '';
}

function extractCopyText(btn, onclick) {
  const dataCopy = btn.getAttribute('data-copy')
    || btn.getAttribute('data-clipboard-text')
    || btn.getAttribute('data-copy-text');
  if (dataCopy) return decodeHtmlEntities(dataCopy);

  if (/mgbCopyScript|copyScript/i.test(onclick || '')) {
    const scriptBox = btn.closest('.mgb-cmd-block, .mgb-step, .mgb-section, .mgb-guide')?.querySelector(
      '#mgb-script-content, .mgb-script-box, pre, code'
    );
    const scriptText = scriptBox?.textContent?.trim();
    if (scriptText) return scriptText;
  }

  const fromOnclick = parseOnclickCopyText(onclick);
  if (fromOnclick) return fromOnclick;

  return findNearestCodeText(btn);
}

function findCopyButton(target) {
  if (!target?.closest) return null;

  const candidates = [
  target.closest('button'),
  target.closest('[role="button"]'),
  target.closest('a[class*="copy"]'),
  target.closest('[class*="copy-btn"]'),
  target.closest('[onclick*="Copy"]'),
  target.closest('[onclick*="copy"]'),
  target.closest('[data-copy]'),
  target.closest('[data-clipboard-text]'),
  ];

  return candidates.find(isCopyButton) || null;
}

function copyWithExecCommand(doc, text) {
  const textarea = doc.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.cssText = 'position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;opacity:0;';
  doc.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let copied = false;
  try {
    copied = doc.execCommand('copy');
  } catch { /* ignore */ }

  doc.body.removeChild(textarea);
  return copied;
}

async function copyText(iframe, text) {
  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win || !text) return false;

  try {
    if (win.navigator.clipboard?.writeText) {
      await win.navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* try fallback */ }

  if (copyWithExecCommand(doc, text)) return true;

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch { /* ignore */ }

  return copyWithExecCommand(document, text);
}

function showCopiedFeedback(btn) {
  const resetHtml = btn.dataset.copyResetHtml || btn.innerHTML;
  if (!btn.dataset.copyResetHtml) btn.dataset.copyResetHtml = resetHtml;

  btn.textContent = '✓ Copied';
  btn.style.color = '#4ade80';
  btn.style.borderColor = '#4ade80';

  window.setTimeout(() => {
    btn.innerHTML = btn.dataset.copyResetHtml;
    btn.style.color = '';
    btn.style.borderColor = '';
  }, 1800);
}

function isCopyButton(btn) {
  if (!btn) return false;

  if (btn.hasAttribute('data-copy') || btn.hasAttribute('data-clipboard-text') || btn.hasAttribute('data-copy-text')) {
    return true;
  }

  const className = btn.className || '';
  if (/copy/i.test(className)) return true;

  const onclick = btn.getAttribute('onclick') || '';
  if (/mgbCopy|clipboard|copyToClipboard|copyText|copyCommand|copyScript/i.test(onclick)) {
    return true;
  }

  const tag = btn.tagName;
  if ((tag === 'BUTTON' || tag === 'A' || btn.getAttribute('role') === 'button') && /\bcopy\b/i.test(btn.textContent || '')) {
    return true;
  }

  return false;
}

function wireIframeCopyHandlers(iframe) {
  const doc = iframe.contentDocument;
  if (!doc) return () => {};

  const onClick = async (event) => {
    const btn = findCopyButton(event.target);
    if (!btn) return;

    const onclick = btn.getAttribute('onclick') || '';
    const text = extractCopyText(btn, onclick);

    // Let embedded onclick/script handlers run when we cannot resolve copy text.
    if (!text) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const copied = await copyText(iframe, text);
    if (copied) {
      showCopiedFeedback(btn);
    } else {
      iframe.contentWindow?.prompt('Copy this text (Ctrl+C / Cmd+C):', text);
    }
  };

  doc.addEventListener('click', onClick, true);
  return () => doc.removeEventListener('click', onClick, true);
}

function buildSrcDoc(html, theme) {
  const previewTheme = theme === 'light' ? 'light' : 'dark';
  const bodyColor = previewTheme === 'light' ? '#111111' : '#eaeaea';

  return [
    '<!DOCTYPE html>',
    `<html data-theme="${previewTheme}">`,
    '<head>',
    '<meta charset="utf-8">',
    `<style>html,body{margin:0;padding:0;background:transparent;color:${bodyColor};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif}</style>`,
    '</head>',
    '<body>',
    html,
    '</body>',
    '</html>',
  ].join('');
}

function HtmlPreview({ html, theme }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(200);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let removeCopyHandlers = () => {};
    let observer;

    const resize = () => {
      try {
        const body = iframe.contentDocument?.body;
        if (body) {
          const nextHeight = body.scrollHeight + 20;
          setHeight(Math.max(nextHeight, 100));
        }
      } catch { /* ignore */ }
    };

    const onLoad = () => {
      removeCopyHandlers();
      removeCopyHandlers = wireIframeCopyHandlers(iframe);
      resize();

      try {
        observer?.disconnect();
        observer = new MutationObserver(resize);
        observer.observe(iframe.contentDocument.body, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      } catch { /* ignore */ }
    };

    iframe.addEventListener('load', onLoad);
    if (iframe.contentDocument?.readyState === 'complete') onLoad();

    return () => {
      iframe.removeEventListener('load', onLoad);
      removeCopyHandlers();
      observer?.disconnect();
    };
  }, [html, theme]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={buildSrcDoc(html, theme)}
      title="config-preview"
      className="config-iframe-preview"
      style={{ height: `${height}px` }}
    />
  );
}

export default function MongoConfiguration() {
  const { theme } = useTheme();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [contentTab, setContentTab] = useState({});

  const getTab = (id) => contentTab[id] || 'data';
  const switchTab = (id, tab) => setContentTab(prev => ({ ...prev, [id]: tab }));

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getMongoConfigs();
      setConfigs(res.data || []);
    } catch {
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAdd = async () => {
    if (!newTitle.trim()) { toast.warning('Title is required'); return; }
    setSaving(true);
    try {
      const res = await createMongoConfig({ title: newTitle, content: newContent });
      setConfigs(prev => [res.data, ...prev]);
      setExpandedIds(prev => ({ ...prev, [res.data._id]: true }));
      setNewTitle('');
      setNewContent('');
      setShowAdd(false);
      toast.success('Configuration added');
    } catch {
      toast.error('Failed to add configuration');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (config) => {
    setEditId(config._id);
    setEditTitle(config.title);
    setEditContent(config.content);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditTitle('');
    setEditContent('');
  };

  const handleUpdate = async () => {
    if (!editTitle.trim()) { toast.warning('Title is required'); return; }
    setSaving(true);
    try {
      const res = await updateMongoConfig(editId, { title: editTitle, content: editContent });
      setConfigs(prev => prev.map(c => c._id === editId ? res.data : c));
      cancelEdit();
      toast.success('Configuration updated');
    } catch {
      toast.error('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this configuration?')) return;
    try {
      await deleteMongoConfig(id);
      setConfigs(prev => prev.filter(c => c._id !== id));
      toast.success('Configuration deleted');
    } catch {
      toast.error('Failed to delete configuration');
    }
  };

  return (
    <div className="mongo-config-page page-container">
      <div className="gcp-page-header">
        <div className="gcp-page-title-row">
          <h1><HiOutlineCog size={28} /> MongoDB Configuration</h1>
          <span className="pv-subtitle">{configs.length} configuration{configs.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="gcp-page-toolbar">
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)} disabled={showAdd}>
            <HiOutlinePlus size={14} /> Add Config
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="config-add-card">
          <div className="config-add-header">
            <h3>New Configuration</h3>
          </div>
          <div className="config-add-body">
            <input
              type="text"
              className="config-input"
              placeholder="Title"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              autoFocus
            />
            <div className="config-content-wrap">
              <div className="config-tab-bar">
                <button className={`config-tab ${getTab('new') === 'data' ? 'active' : ''}`} onClick={() => switchTab('new', 'data')}>Data</button>
                <button className={`config-tab ${getTab('new') === 'html' ? 'active' : ''}`} onClick={() => switchTab('new', 'html')}>HTML</button>
              </div>
              {getTab('new') === 'html' ? (
                <textarea
                  className="config-textarea"
                  placeholder="Write HTML code here..."
                  value={newContent}
                  onChange={e => setNewContent(e.target.value)}
                  rows={6}
                />
              ) : newContent ? (
                <HtmlPreview html={newContent} theme={theme} />
              ) : (
                <div className="config-data-preview">
                  <span className="config-empty">Add HTML in the HTML tab to see rendered content here</span>
                </div>
              )}
            </div>
          </div>
          <div className="config-add-footer">
            <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving}>
              <HiOutlineCheck size={14} /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowAdd(false); setNewTitle(''); setNewContent(''); }}>
              <HiOutlineX size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <div className="loading-bar"><div className="loading-bar-inner" /></div>}

      {!loading && configs.length === 0 && !showAdd && (
        <div className="empty-state"><p>No configurations yet. Click <strong>Add Config</strong> to create one.</p></div>
      )}

      <div className="config-list">
        {configs.map(config => {
          const isExpanded = !!expandedIds[config._id];
          const isEditing = editId === config._id;

          return (
            <div key={config._id} className={`config-card ${isExpanded ? 'expanded' : ''}`}>
              <div className="config-card-header" onClick={() => !isEditing && toggleExpand(config._id)}>
                <div className="config-card-left">
                  <span className="config-chevron">
                    {isExpanded ? <HiOutlineChevronDown size={16} /> : <HiOutlineChevronRight size={16} />}
                  </span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="config-input config-inline-edit"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <span className="config-card-title">{config.title}</span>
                  )}
                </div>
                <div className="config-card-actions" onClick={e => e.stopPropagation()}>
                  {isEditing ? (
                    <>
                      <button className="btn-icon save" onClick={handleUpdate} disabled={saving} title="Save">
                        <HiOutlineCheck size={16} />
                      </button>
                      <button className="btn-icon cancel" onClick={cancelEdit} title="Cancel">
                        <HiOutlineX size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn-icon" onClick={() => { if (!isExpanded) toggleExpand(config._id); startEdit(config); }} title="Edit">
                        <HiOutlinePencil size={15} />
                      </button>
                      <button className="btn-icon danger" onClick={() => handleDelete(config._id)} title="Delete">
                        <HiOutlineTrash size={15} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="config-card-body">
                  <div className="config-content-wrap">
                    <div className="config-tab-bar">
                      <button className={`config-tab ${getTab(config._id) === 'data' ? 'active' : ''}`} onClick={() => switchTab(config._id, 'data')}>Data</button>
                      <button className={`config-tab ${getTab(config._id) === 'html' ? 'active' : ''}`} onClick={() => switchTab(config._id, 'html')}>HTML</button>
                    </div>
                    {getTab(config._id) === 'html' ? (
                      isEditing ? (
                        <textarea
                          className="config-textarea"
                          value={editContent}
                          onChange={e => setEditContent(e.target.value)}
                          rows={8}
                        />
                      ) : (
                        <pre className="config-content">{config.content || <span className="config-empty">No HTML content</span>}</pre>
                      )
                    ) : (isEditing ? editContent : config.content) ? (
                      <HtmlPreview html={isEditing ? editContent : config.content} theme={theme} />
                    ) : (
                      <div className="config-data-preview">
                        <span className="config-empty">No content</span>
                      </div>
                    )}
                  </div>
                  <div className="config-card-meta">
                    Created {new Date(config.createdAt).toLocaleString()}
                    {config.updatedAt !== config.createdAt && ` · Updated ${new Date(config.updatedAt).toLocaleString()}`}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
